import { useMemo } from 'react';
import { PieChart, Pie, Cell } from 'recharts';
import { formatNum } from '@app/utils/math';
import { StatusBadgeWithIcon, TextAlign, TextSize, Typography, STATUS } from '@shared/index';
import { TeamQuarterlyHistory } from '@entities/managers-perfomance/types';

type Props = {
  quarter: number;
  item: TeamQuarterlyHistory | null;
  currentYear: number;
  currentQuarter: number;
  year: number;
};

export type QuarterStatus =
  | typeof STATUS.SUCCESS
  | typeof STATUS.IN_PROCESS
  | typeof STATUS.NOT_COMPLETED;

export const getCurrentQuarter = () => {
  const month = new Date().getMonth() + 1;

  if (month <= 3) return 1;
  if (month <= 6) return 2;
  if (month <= 9) return 3;

  return 4;
};

const getQuarterStatus = (
  item: TeamQuarterlyHistory | null,
  quarter: number,
  currentYear: number,
  currentQuarter: number,
  year: number
): QuarterStatus => {
  if (year === currentYear && quarter === currentQuarter) {
    return STATUS.IN_PROCESS;
  }

  if (!item) {
    return STATUS.NOT_COMPLETED;
  }

  if (item.percentage >= 100) {
    return STATUS.SUCCESS;
  }

  return STATUS.NOT_COMPLETED;
};

const getProgressColor = (status: QuarterStatus): string => {
  switch (status) {
    case STATUS.SUCCESS:
      return '#00C950';

    case STATUS.NOT_COMPLETED:
      return '#7E7D7E';

    case STATUS.IN_PROCESS:
      return '#4598E6';

    default:
      return '#2E2E2E';
  }
};

const CIRCLE_SIZE = 180;
const INNER_RADIUS = 65;
const OUTER_RADIUS = 75;
const INNER_DIAMETER = INNER_RADIUS * 2;
const REMAINING_COLOR = '#2E2E2E';

export const QuarterlyHistoryCard = ({
  quarter,
  item,
  currentYear,
  currentQuarter,
  year,
}: Props) => {
  const status = getQuarterStatus(item, quarter, currentYear, currentQuarter, year);
  const progressColor = getProgressColor(status);

  const percent = item ? Math.min(100, Math.max(0, item.percentage)) : 0;
  const displayPercent = percent >= 100 ? Math.round(percent).toString() : percent.toFixed(0);
  const displayValue = item ? formatNum(item.hashrate) : '0';

  const chartData = useMemo(() => {
    if (item) {
      return [
        { name: 'progress', value: percent },
        { name: 'remaining', value: 100 - percent },
      ];
    }

    return [{ name: 'remaining', value: 100 }];
  }, [item, percent]);

  const infoCards = [{ label: 'План', value: formatNum(item?.plan), unit: 'Ph' }];

  const backgroundColors: Record<(typeof STATUS)[keyof typeof STATUS], string> = {
    [STATUS.SUCCESS]: '#161917',
    [STATUS.NOT_COMPLETED]: '#1D1616',
    [STATUS.IN_PROCESS]: '#141316',
  };

  const backgroundColor = backgroundColors[status] ?? '#141316';

  return (
    <div
      className="flex justify-between p-5 rounded-2xl border border-gray-600"
      style={{ backgroundColor }}
    >
      <div className="flex flex-col justify-between gap-4">
        <div className="flex items-center gap-4">
          <Typography
            title={`Q${quarter}`}
            size={TextSize.size18}
            className="text-white font-bold"
          />
          <StatusBadgeWithIcon status={status} />
        </div>

        <div className="flex flex-col gap-2">
          {infoCards.map(card => (
            <div key={card.label} className="flex items-center gap-2">
              <Typography
                size={TextSize.size14}
                className="text-gray-400 font-medium"
                text={`${card.label}:`}
              />
              <div className="flex gap-1 items-start">
                <Typography
                  size={TextSize.size16}
                  className="text-white font-semibold"
                  text={card.value}
                />
                <Typography
                  size={TextSize.size12}
                  className="text-gray-400 font-medium"
                  text={card.unit}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="relative flex items-center justify-center flex-shrink-0">
        <PieChart width={CIRCLE_SIZE} height={CIRCLE_SIZE}>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={INNER_RADIUS}
            outerRadius={OUTER_RADIUS}
            startAngle={90}
            endAngle={-270}
            dataKey="value"
            stroke="none"
          >
            {chartData.map((entry, index) => (
              <Cell
                key={index}
                fill={entry.name === 'progress' ? progressColor : REMAINING_COLOR}
              />
            ))}
          </Pie>
        </PieChart>
        <div
          className="absolute top-1/2 left-1/2 rounded-full"
          style={{
            width: INNER_DIAMETER,
            height: INNER_DIAMETER,
            backgroundColor: '#1D1616',
            transform: 'translate(-50%, -50%)',
            zIndex: 1,
          }}
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 text-center">
          <Typography
            text={`${displayPercent}%`}
            size={TextSize.size22}
            className="text-white font-semibold"
            align={TextAlign.CENTER}
          />
          <Typography
            text={`${displayValue} Ph`}
            size={TextSize.size10}
            className="text-gray-100 font-medium"
            align={TextAlign.CENTER}
          />
        </div>
      </div>
    </div>
  );
};
